import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';

import { CONFETTI_COLORS } from '../confetti/confetti.component';

/**
 * Milliseconds until the whole timeline has played and the button can be pressed.
 * **Must match the SCSS**: `$t-thanks` (2.5s) + the last staggered entry (0.4s) + its duration (0.5s).
 */
const READY_MS = 3400;

const SPARK_COUNT = 44;

/** Longest a survey name may get before it is cut: the card has a fixed height (see the SCSS). */
const MAX_NAME_LENGTH = 80;

/** One firework spark. Plain numbers: the SCSS turns them into a travel, nothing runs per frame. */
export interface SurveySentSpark {
  /** Degrees, 0 pointing right. */
  ang: number;
  /** How far it flies, in px at the reference (desktop) size — the SCSS scales it by `--u`. */
  dist: number;
  /** Streak length, same units. */
  len: number;
  color: string;
  /** Every other spark also throws a small dot. */
  dot: boolean;
}

/** Deterministic pseudo-random in [0, 1): the same burst every time, no `Math.random()` per mount. */
const rnd = (i: number): number => {
  const x = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};

const buildSparks = (): SurveySentSpark[] =>
  Array.from({ length: SPARK_COUNT }, (_, i) => ({
    ang: ((i / SPARK_COUNT) * Math.PI * 2 + rnd(i) * 0.3) * (180 / Math.PI),
    dist: 380 * (0.65 + rnd(i + 7) * 0.45),
    len: (28 + rnd(i + 3) * 22) * 1.5,
    color: CONFETTI_COLORS[Math.floor(rnd(i + 11) * CONFETTI_COLORS.length)],
    dot: i % 2 === 0,
  }));

/**
 * The "your survey was sent" moment: the screen dims, a badge pops with a firework, the envelope
 * leaves as a comet and a check lands, then the badge rises and becomes the card that thanks the
 * learner.
 *
 * Reusable and knows nothing about courses: mount it with an `*ngIf` and it plays from its creation;
 * it tells the host when it is closed ({@link dismissed}) and the host removes it.
 *
 * The whole timeline is declared in the SCSS (`$t-appear` … `$t-thanks`), one keyframe animation per
 * element, so it can be read and tuned in one place. The only timer here is {@link READY_MS}, which
 * enables the button once the last entry has landed. It can be closed at any time — X, Escape, a tap
 * on the dimmed area or the hardware back button — so nobody has to sit through it.
 *
 * `prefers-reduced-motion` skips every animation and shows the final card straight away.
 */
@Component({
  selector: 'app-survey-sent-celebration',
  templateUrl: './survey-sent-celebration.component.html',
  styleUrls: ['./survey-sent-celebration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SurveySentCelebrationComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Name of the survey that was sent, printed in bold inside the message. */
  @Input()
  set surveyName(value: string) {
    const name = (value ?? '').trim();
    this.name = name.length > MAX_NAME_LENGTH ? `${name.slice(0, MAX_NAME_LENGTH - 1).trimEnd()}…` : name;
  }

  /** The learner closed it. The host clears whatever mounted this component. */
  @Output() dismissed = new EventEmitter<void>();

  @ViewChild('action') private action?: ElementRef<HTMLButtonElement>;
  @ViewChild('root', { static: true }) private root!: ElementRef<HTMLElement>;
  @ViewChild('card', { static: true }) private card!: ElementRef<HTMLElement>;

  name = '';
  /** `true` once the timeline has finished: the button is pressable. */
  ready = false;
  readonly reduced = prefersReducedMotion();
  readonly sparks = buildSparks();

  private closed = false;
  private timer?: ReturnType<typeof setTimeout>;
  private backButton?: Subscription;
  private cardObserver?: ResizeObserver;

  constructor(private cdr: ChangeDetectorRef, private platform: Platform, private zone: NgZone) {}

  ngOnInit(): void {
    if (this.reduced) {
      this.ready = true;
    } else {
      this.timer = setTimeout(() => {
        this.ready = true;
        this.cdr.markForCheck();
        // One frame later: the button is only enabled once the change detection above has run.
        requestAnimationFrame(() => this.action?.nativeElement.focus());
      }, READY_MS);
    }

    // Above Ionic's own overlays (100): while this is open, "back" closes it instead of leaving the page.
    this.backButton = this.platform.backButton.subscribeWithPriority(101, () => this.close());
  }

  ngAfterViewInit(): void {
    this.measureCard();
    // The card's height follows its text (a long name, a narrower screen, the font arriving late), and
    // the badge's rise follows the card: keep them in step. Outside the zone, nothing here is Angular's.
    if (typeof ResizeObserver === 'undefined') return;
    this.zone.runOutsideAngular(() => {
      this.cardObserver = new ResizeObserver(() => this.measureCard());
      this.cardObserver.observe(this.card.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.cardObserver?.disconnect();
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.backButton?.unsubscribe();
  }

  @HostListener('document:keydown.escape')
  close(): void {
    // Guarded: Escape and the back button can both arrive for the same press.
    if (this.closed) return;
    this.closed = true;
    this.dismissed.emit();
  }

  /**
   * The card has no fixed height — it fits its content — but the badge has to rise exactly to the card's
   * top, and CSS cannot read an element's height into a `calc()` elsewhere. So it is written down:
   * `--card-h` feeds the rise in the SCSS. `offsetHeight` ignores transforms and clips, so it is the
   * layout height even in the middle of the animation.
   */
  private measureCard(): void {
    this.root.nativeElement.style.setProperty('--card-h', `${this.card.nativeElement.offsetHeight}px`);
  }

  trackSpark(index: number): number {
    return index;
  }
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
}
